import queryString from 'query-string';
import { select } from 'redux-saga/effects';
import Cookies from 'js-cookie';
import { head } from 'lodash';
import { DOMAIN, ENVIRONMENT, API_URL } from 'settings';
import jwtDecode from 'jwt-decode';
import { notification } from 'antd';
import { getValidAccessToken } from 'services/Api/refreshHelper';
import { browserId, beginPresenceLogin, claimActivity, acceptPresenceResponse, presenceRejected, notifyPresenceLogout } from 'services/presence';
import { rejectBrowserLogin } from 'services/logoutSync';
import { currentLogin } from 'services/loginProtocol';
import { bootstrapMetadata, legacyRetrieved, failedJoin } from 'services/monitoring';

type apiParams<Payload> = {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: Payload;
  token?: string;
  apiUrl: string;
  keepalive?: boolean;
  signal?: AbortSignal;
};

let refreshingPromise: Promise<string> | null = null;

function refreshAccessToken(): Promise<string> {
  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      const resp = await fetch(`${API_URL}/auth/refresh_token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refresh: Cookies.get(`pm_${ENVIRONMENT}_refresh`),
        }),
      });

      if (!resp.ok) {
        Cookies.remove(`pm_${ENVIRONMENT}_token`, { domain: DOMAIN });
        Cookies.remove(`pm_${ENVIRONMENT}_refresh`, { domain: DOMAIN });
        Cookies.remove(`pm_${ENVIRONMENT}_refresh_exp`, { domain: DOMAIN });
        Cookies.remove(`pm_${ENVIRONMENT}_token_exp`, { domain: DOMAIN });
        refreshingPromise = null;
        window.location.href = '/auth/login';
        notification.warning({ message: 'Session expired' });
        throw new Error('Session expired.');
      }

      const { access } = await resp.json();
      const decoded = jwtDecode<{ exp: number }>(access);

      Cookies.set(`pm_${ENVIRONMENT}_token`, access, {
        sameSite: 'Strict',
        domain: DOMAIN,
      });
      Cookies.set(`pm_${ENVIRONMENT}_token_exp`, decoded.exp.toString(), {
        sameSite: 'Strict',
        domain: DOMAIN,
      });

      refreshingPromise = null;
      return access;
    })();
  }
  return refreshingPromise;
}

export default async function apiCaller<Payload>({
  path,
  method = 'GET',
  data,
  // token,
  apiUrl,
  keepalive,
  signal,
}: apiParams<Payload>) {
  const query = '?' + queryString.stringify((data as any) || {});

  let url = `${apiUrl}${path}`;
  if (!url.endsWith('/')) url += '/';

  if (method === 'GET') url += query;

  const urlParams = path.split('/').filter(s => s[0] === ':');

  urlParams.forEach(param => {
    const key = param.slice(1);
    url = url.replace(param, (data as any)[key]);
    (data as any)[key] = undefined;
  });
  const bootstrapSession = method === 'GET'
    ? Number(url.split('?')[0].match(/\/sessions\/(\d+)\/$/)?.[1])
    : 0;
  const bootstrap = bootstrapSession ? bootstrapMetadata(bootstrapSession) : undefined;
  if (bootstrap)
    url += `${url.includes('?') ? '&' : '?'}monitoring_join_id=${encodeURIComponent(bootstrap.join_id)}`;
  const token = await getValidAccessToken();
  // const token = Cookies.get(`pm_${ENVIRONMENT}_token`);
  // if (token) {
  //   const { exp } = jwtDecode<{ exp: number }>(token);
  //   const now = Date.now() / 1000;
  //   if (exp - now < 30) {
  //     console.log('token expired, trying to refresh');
  //     // if token expired or about to expire, first refresh it
  //     token = await refreshAccessToken();
  //   } else {
  //     console.log('token not expired');
  //   }
  // }

  const headers = new Headers();
  const activity = await claimActivity(token);
  if (activity) headers.set('X-PM-Activity', activity.marker);
  if (method === 'POST' && url.split('?')[0].endsWith('/auth/get_token/')) {
    const browser_id = await browserId();
    data = { ...data, browser_id } as Payload;
  }
  // headers.append('Content-Type', 'application/json');
  const isForm = data instanceof FormData;
  if (!isForm) {
    // only set JSON header if we’re sending a normal JSON object
    headers.append('Content-Type', 'application/json');
  }

  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
    // headers.append('Authorization', `Bearer fake_token`);
  }

  const response = await fetch(url, {
    headers,
    method,
    keepalive,
    signal,
    // this might actually not be used in prod/stage, only for local
    // credentials: 'include',
    // body: method !== 'GET' ? JSON.stringify(data) : undefined,
    body:
      method !== 'GET'
        ? isForm
          ? (data as unknown as FormData)
          : JSON.stringify(data)
        : undefined,
  }).catch(reason => {
    if (bootstrap) failedJoin(bootstrapSession, bootstrap.join_id);
    throw reason;
  });
  acceptPresenceResponse(response, token, activity);

  let res = null;
  try {
    res = await response.json();
  } catch (err) {
    // empty response
  }
  if (bootstrap) {
    if (response.ok && res) legacyRetrieved(bootstrapSession, bootstrap.join_id);
    else failedJoin(bootstrapSession, bootstrap.join_id);
  }

  if (res?.access && url.split('?')[0].endsWith('/auth/get_token/')) {
    if (!currentLogin(res.access) || !currentLogin(res.refresh) || !res.presence)
      throw new Error('Please reload the page and sign in again.');
    try {
      beginPresenceLogin(res.access, res.presence);
    } catch (error) {
      // A failed local setup must not leave a successful but unusable login.
      notifyPresenceLogout(res.refresh);
      throw error;
    }
  }
  if (response.status === 401 && res?.code === 'presence_expired') {
    presenceRejected(token);
    rejectBrowserLogin(token);
  }

  try {
    return { result: res, status: response.status };
  } catch (e) {
    return { result: e, status: response.status };
  }
}
